import { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, CardActionArea, CardContent, Chip, Grid, MenuItem, Pagination, Paper, Typography } from "@mui/material";
import Add from "@mui/icons-material/Add";
import { ebookStatuses, type EbookListResponse, type EbookStatus, type PlanEntitlement } from "@ebook/contracts";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { api } from "../lib/api-client";
import { EmptyState, PageError, PageLoading } from "../components/PageState";
import { StatusChip } from "../components/StatusChip";
import { GlobalInput } from "../components/GlobalInput";
import { AddEbookDialog } from "../components/AddEbookDialog";
import { useTranslate } from "../i18n";

function statusLabelKey(status: EbookStatus): string {
  return status[0]!.toUpperCase() + status.slice(1);
}

function parseStatuses(raw: string | undefined): EbookStatus[] {
  if (!raw) return [];
  return raw.split(",").filter((value): value is EbookStatus => (ebookStatuses as readonly string[]).includes(value));
}

export function EbooksPage() {
  const routeSearch = useSearch({ from: "/_app/ebooks" });
  const [data, setData] = useState<EbookListResponse | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<EbookStatus | "">("");
  const [linkedFilter, setLinkedFilter] = useState<{ statuses: EbookStatus[]; label: string } | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [entitlement, setEntitlement] = useState<PlanEntitlement | null>(null);
  const navigate = useNavigate();
  const __ = useTranslate();

  useEffect(() => {
    const statuses = parseStatuses(routeSearch.status);
    if (!statuses.length) return;
    if (statuses.length === 1) { setStatus(statuses[0]!); setLinkedFilter(null); }
    else setLinkedFilter({ statuses, label: routeSearch.label ?? statuses.map((value) => __(statusLabelKey(value))).join(", ") });
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSearch.status, routeSearch.label]);

  const load = useCallback(async () => {
    setError("");
    const query = new URLSearchParams({ page: String(page), pageSize: "12", search });
    if (linkedFilter) query.set("status", linkedFilter.statuses.join(","));
    else if (status) query.set("status", status);
    try {
      const [ebooks, quota] = await Promise.all([api.ebooks(`?${query}`), api.entitlement()]);
      setData(ebooks); setEntitlement(quota);
    } catch (caught) { setError(caught instanceof Error ? caught.message : __("โหลดรายการไม่สำเร็จ")); }
  }, [__, page, search, status, linkedFilter]);
  useEffect(() => { const timer = setTimeout(() => void load(), 250); return () => clearTimeout(timer); }, [load]);
  const activeFilterLabel = linkedFilter ? linkedFilter.label : status ? __(statusLabelKey(status)) : "";
  return <Box>
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2}>
      <Typography variant="h4" fontWeight={800}>{__("Ebooks")}</Typography>
      <Button variant="contained" startIcon={<Add />} disabled={Boolean(entitlement?.booksLimit !== null && entitlement && entitlement.booksUsed >= entitlement.booksLimit)} onClick={() => setAddOpen(true)}>{__("Add Ebook")}</Button>
    </Box>
    <Paper variant="outlined" sx={{ p: 2, mb: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box display="flex" gap={2} flexWrap="wrap">
        <GlobalInput label={__("ค้นหาชื่อ")} value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} sx={{ minWidth: 260, flex: 1 }} />
        <GlobalInput select label={__("สถานะ")} value={status} onChange={(event) => { setStatus(event.target.value as EbookStatus | ""); setLinkedFilter(null); setPage(1); }} sx={{ minWidth: 170 }}>
          <MenuItem value="">{__("ทั้งหมด")}</MenuItem>
          {ebookStatuses.map((value) => <MenuItem key={value} value={value}>{__(statusLabelKey(value))}</MenuItem>)}
        </GlobalInput>
      </Box>
      {activeFilterLabel && <Box><Chip label={__("สถานะ: {{status}}", { status: activeFilterLabel })} size="small" onDelete={() => { setStatus(""); setLinkedFilter(null); setPage(1); }} /></Box>}
    </Paper>
    {error ? <PageError message={error} onRetry={() => void load()} /> : !data ? <PageLoading /> : data.items.length === 0 ? <EmptyState title={__("ไม่พบรายการ E-book")} description={__("ลองเปลี่ยนตัวกรองหรือเพิ่ม E-book ใหม่")} /> :
      <><Grid container spacing={2.5}>{data.items.map((ebook) => <Grid key={ebook.id} size={{ xs: 12, sm: 6, lg: 4, xl: 3 }}>
        <Card variant="outlined" sx={{ height: "100%" }}><CardActionArea onClick={() => void navigate({ to: "/ebooks/$ebookId", params: { ebookId: ebook.id }, search: { tab: "general" } })}>
          <Box component={ebook.thumbnailUrl ? "img" : "div"} src={ebook.thumbnailUrl ?? undefined} alt="" sx={{ width: "100%", aspectRatio: "4/3", objectFit: "contain", bgcolor: "grey.100" }} />
          <CardContent><Box display="flex" justifyContent="space-between" gap={1}><Typography fontWeight={700} noWrap>{ebook.title}</Typography><StatusChip status={ebook.status} /></Box><Typography variant="body2" color="text.secondary" mt={1}>{__("{{count}} หน้า", { count: ebook.pageCount })}</Typography></CardContent>
        </CardActionArea></Card>
      </Grid>)}</Grid>
      <Pagination count={Math.max(1, Math.ceil(data.total / data.pageSize))} page={page} onChange={(_, value) => setPage(value)} sx={{ mt: 4, display: "flex", justifyContent: "center" }} /></>}
    <AddEbookDialog
      open={addOpen}
      pageLimit={entitlement?.pagesPerBook ?? 3000}
      onClose={() => { setAddOpen(false); void load(); }}
      onCreated={(ebookId) => {
        setAddOpen(false);
        void navigate({ to: "/ebooks/$ebookId", params: { ebookId }, search: { tab: "general" } });
      }}
    />
  </Box>;
}
